import React from 'react';
import type { ReportView } from 'shared-models/browser';
import { formatMoney, isolateAmountLtr, roundMoney } from '../../../../utils/money';
import classes from './pos-report-view.module.scss';

const REPORT_TITLES: Record<ReportView['kind'], string> = {
  x: 'דוח X',
  z: 'דוח Z',
  worker: 'דוח עובד',
};

const pad = (n: number) => String(n).padStart(2, '0');
const formatDateTime = (iso: string | null): string => {
  if (!iso) return '--';
  const d = new Date(iso);
  return `${pad(d.getDate())}-${pad(d.getMonth() + 1)}-${String(d.getFullYear()).slice(2)} ${pad(d.getHours())}:${pad(d.getMinutes())}`;
};

const Money: React.FC<{ value: number }> = ({ value }) => <span>{isolateAmountLtr(formatMoney(value))} ₪</span>;

const Row: React.FC<{ label: string; value: React.ReactNode; bold?: boolean; dataTestId?: string }> = ({ label, value, bold, dataTestId }) => (
  <div className={bold ? `${classes.row} ${classes.rowBold}` : classes.row} data-testid={dataTestId}>
    <span className={classes.rowLabel}>{label}</span>
    <span className={classes.rowValue}>{value}</span>
  </div>
);

const SectionTitle: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <div className={classes.sectionTitle}>{children}</div>
);

export type PosReportViewProps = {
  report: ReportView;
  innerRef?: React.Ref<HTMLDivElement>;
  variant?: 'screen' | 'print';
};

// Summary: Presentational X/Z/worker report — renders a ReportView per the locked layout (RTL).
// Details: docs/function-library/web/contexts-pages.md#pages-posshellpage-components-reports
export const PosReportView: React.FC<PosReportViewProps> = ({ report, innerRef, variant = 'screen' }) => {
  const r = report;
  const printableZCloseTime =
    r.kind === 'z' && variant === 'print'
      ? (r.closeTime ?? new Date().toISOString())
      : r.closeTime;
  // Open-orders figures only apply to the live X snapshot; Z/worker sessions are already closed.
  const showOpenOrders = r.kind === 'x';
  // Net cash across drawers; drives both the total row and the expected-cash row (compute once).
  const drawerNetCash = r.drawers.reduce((sum, d) => sum + d.cashNetOfTips, 0);
  return (
    <div ref={innerRef} className={variant === 'print' ? `${classes.paper} ${classes.paperPrint}` : classes.paper}>
      <div className={classes.header}>
        {r.header.split('\n').map((line, index) => (
          <span key={index} className={classes.businessLine}>{line}</span>
        ))}
      </div>

      <div className={classes.metaBlock}>
        <div className={classes.metaRow}>מספר משמרת: {r.sessionName ?? '--'}</div>
        <div className={classes.metaRow}>זמן פתיחת משמרת: {formatDateTime(r.openTime)}</div>
        {r.kind === 'z' && <div className={classes.metaRow}>זמן סגירת משמרת: {formatDateTime(printableZCloseTime)}</div>}
        {r.kind !== 'z' && <div className={classes.metaRow}>זמן הפקה: {formatDateTime(r.printTime)}</div>}
        {r.workerName && <div className={classes.metaRow}>עובד: {r.workerName}</div>}
        <div className={classes.metaRow}>משתמש: {r.openerName ?? '--'}</div>
      </div>

      <div className={classes.title}>{REPORT_TITLES[r.kind]}</div>

      <div className={classes.section}>
        {showOpenOrders && (
          <Row label="סה&quot;כ מכירות עם הזמנות פתוחות (לא כולל טיפים)" value={<Money value={r.salesWithOpenOrders} />} />
        )}
        <Row label="סה&quot;כ הכנסות - הזמנות סגורות (לא כולל טיפים)" value={<Money value={r.closedRevenue} />} />
        {showOpenOrders && (
          <Row label="סכום כולל הזמנות פתוחות (לא כולל טיפים)" value={<Money value={r.openOrdersTotal} />} />
        )}
        {showOpenOrders && <Row label="כמות הזמנות פתוחות" value={r.openOrdersCount} />}
        <Row label={`מע"מ ${r.vatRatePercent}%`} value={<Money value={r.vatAmount} />} />
        <Row label="סה&quot;כ סועדים" value={r.dinersCount} />
        <Row label="ביטול פריטים (כמות)" value={r.canceledItemsCount} />
        <Row label="ביטול פריטים (סכום)" value={<Money value={r.canceledItemsAmount} />} />
        <Row label="הנחות (סכום)" value={<Money value={r.discountsAmount} />} />
        <Row label="הזמנות מבוטלות (כמות)" value={r.canceledOrdersCount} />
        <Row label="ממוצע לסועד (לא כולל טיפים)" value={<Money value={r.avgPerDiner} />} />
        <Row label="ממוצע לסועד - מהירה" value={<Money value={r.avgPerDinerTakeAway} />} />
        <Row label="סה&quot;כ טיפים (סכום טיפים כולל)" value={<Money value={r.tipsTotal} />} />
      </div>

      <SectionTitle>פירוט הכנסות לפי קטגוריה</SectionTitle>
      <div className={classes.section}>
        <Row label="אוכל" value={<Money value={r.salesByReportGroup.food} />} />
        <Row label="משקאות" value={<Money value={r.salesByReportGroup.beverages} />} />
        <Row label="אחר" value={<Money value={r.salesByReportGroup.other} />} />
      </div>

      <SectionTitle>טיפים לפי סוג תשלום</SectionTitle>
      <div className={classes.section}>
        <Row label="טיפים אשראי" value={<Money value={r.tipsCredit} />} />
        <Row label="טיפים מזומן" value={<Money value={r.tipsCash} />} />
        <Row label="סה&quot;כ טיפים" value={<Money value={r.tipsTotal} />} bold dataTestId={variant === 'screen' ? 'report-tips-total' : undefined} />
      </div>

      <SectionTitle>סיכום סוגי תקבולים</SectionTitle>
      <div className={classes.section}>
        <Row label="תקבול מזומן (כולל טיפים)" value={<Money value={r.receiptsCash} />} />
        <Row label="תקבול אשראי (כולל טיפים)" value={<Money value={r.receiptsCredit} />} />
        <Row label="סה&quot;כ תקבולים" value={<Money value={r.receiptsTotal} />} bold />
      </div>

      <SectionTitle>סיכום סוגי תשלומים</SectionTitle>
      <div className={classes.section}>
        <Row label="אשראי (לא כולל טיפים)" value={<Money value={r.paymentsCredit} />} />
        <Row label="מזומן (לא כולל טיפים)" value={<Money value={r.paymentsCash} />} />
        <Row label="סה&quot;כ סוגי תשלום" value={<Money value={r.paymentsTotal} />} bold />
      </div>

      <SectionTitle>פירוט סוגי תשלומים</SectionTitle>
      <div className={classes.section}>
        <div className={classes.subHeader}>אשראי</div>
        <Row label="סה&quot;כ חיובים (לא כולל טיפים)" value={<Money value={r.creditCharges} />} />
        <Row label="סה&quot;כ זיכויים" value={<Money value={r.creditRefunds} />} />
        <Row label="סה&quot;כ אשראי" value={<Money value={r.creditTotal} />} bold />
        <div className={classes.subHeader}>מזומן</div>
        <Row label="סה&quot;כ חיובים (לא כולל טיפים)" value={<Money value={r.cashCharges} />} />
        <Row label="סה&quot;כ זיכויים" value={<Money value={r.cashRefunds} />} />
        <Row label="סה&quot;כ מזומן" value={<Money value={r.cashTotal} />} bold />
      </div>

      <SectionTitle>חשבוניות שהופקו</SectionTitle>
      <div className={classes.section}>
        <Row label="עסקאות חיוב" value={r.chargeTransactionsCount} />
        <Row label="עסקאות זיכוי" value={r.refundTransactionsCount} />
      </div>

      <SectionTitle>פירוט מגירות מזומן</SectionTitle>
      <div className={classes.section}>
        {r.drawers.length === 0 && r.openingCash === 0 ? (
          <div className={classes.emptyRow}>אין תנועות מזומן</div>
        ) : (
          <>
            {r.drawers.map((drawer) => (
              <React.Fragment key={drawer.printerId ?? 'none'}>
                <div className={classes.subHeader}>{drawer.printerName}</div>
                <Row label="סה&quot;כ מזומן (כולל טיפים)" value={<Money value={drawer.cashTotalWithTips} />} />
                <Row label="מתוכם סה&quot;כ טיפים" value={<Money value={drawer.tipsTotal} />} />
              </React.Fragment>
            ))}
            {r.openingCash !== 0 && <Row label="מזומן פתיחה" value={<Money value={r.openingCash} />} bold />}
            <Row
              label="סה&quot;כ מזומן בהפחתת טיפים אשראי ומזומן"
              value={<Money value={roundMoney(r.paymentsCash - r.tipsCredit)} />}
              bold
            />
            {r.openingCash !== 0 && (
              <Row
                label="מזומן צפוי בקופה"
                value={<Money value={roundMoney(r.openingCash + drawerNetCash)} />}
                bold
              />
            )}
          </>
        )}
      </div>

      <div className={classes.footer}>
        <div className={classes.footerStars}>* * * * *</div>
        <div>תודה לך</div>
      </div>

      <div className={classes.poweredBy}>Powered by CommunicateAI</div>
    </div>
  );
};
